<script setup lang="ts">
import { useForm } from "@inertiajs/vue3"
import { CsrfField } from "@/components/csrf-field"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AuthLayout from "@/layouts/AuthLayout.vue"

defineOptions({
    layout: [AuthLayout],
})

type Props = {
    links: Links
    email?: string
}

const { links, email } = defineProps<Props>()

const form = useForm({
    email: email ?? "",
    password: "",
})

function submit() {
    form.post(links.store_path)
}
</script>

<template>
    <div class="grid gap-6">
        <div class="space-y-1">
            <h2 class="font-recipe text-2xl font-bold tracking-tight text-[#f4e6d0]">
                Sign in
            </h2>
            <p class="text-sm text-[#d8c7ae]/75">
                Enter your email and password to continue.
            </p>
        </div>

        <form class="grid gap-4" method="POST" @submit.prevent="submit">
            <CsrfField />

            <div class="grid gap-2">
                <Label for="email" class="text-[#e8dcc8]">Email</Label>
                <Input
                    id="email"
                    v-model="form.email"
                    type="email"
                    autocomplete="email"
                    autocapitalize="none"
                    autocorrect="off"
                    placeholder="name@example.com"
                    required
                    class="h-11 border-[#c9a56a]/25 bg-[#0f0b07]/60 text-[#f7f1e8] placeholder:text-[#d8c7ae]/40 focus-visible:ring-[rgb(181_115_5)]"
                />
                <p v-if="form.errors.email" class="text-sm text-red-400">{{ form.errors.email }}</p>
            </div>

            <div class="grid gap-2">
                <Label for="password" class="text-[#e8dcc8]">Password</Label>
                <Input
                    id="password"
                    v-model="form.password"
                    type="password"
                    autocomplete="current-password"
                    placeholder="••••••••"
                    required
                    class="h-11 border-[#c9a56a]/25 bg-[#0f0b07]/60 text-[#f7f1e8] placeholder:text-[#d8c7ae]/40 focus-visible:ring-[rgb(181_115_5)]"
                />
                <p v-if="form.errors.password" class="text-sm text-red-400">{{ form.errors.password }}</p>
            </div>

            <Button
                type="submit"
                :disabled="form.processing"
                class="mt-2 h-11 bg-[rgb(181_115_5)] text-[#140e09] hover:bg-[rgb(212_160_70)]"
            >
                {{ form.processing ? "Signing in…" : "Sign in" }}
            </Button>
        </form>
    </div>
</template>
